import React, {useContext, useEffect, useState, useCallback} from "react";
import BedRockContext from "../../BedRockContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {defaultList} from "../generic/InitialStates";
import {ENTITY_ID_ROLE} from "../generic/Constants";
import {genericDelete, genericGetAll} from "../generic/crud";
import {RoleAddEditDialog} from "./RoleAddEditDialog";
import {showConfirmDialog} from "../../generic-components/ConfirmDialog";
import {showToast, TOAST_SUCCESS} from "../../utils/toast/Toast";
import {useGenericSync} from "../../hooks/useGenericSync";

export function RolesPage() {
    const myData = useContext(BedRockContext);
    const [reload, setReload] = useState(1);
    const [pageLoadComplete, setPageLoadComplete] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showModelId, setShowModelId] = useState(null);
    const [dataList, setDataList] = useState(defaultList);
    const [isLoading, setIsLoading] = useState(false);
    const [sizePerPage, setSizePerPage] = useState(1000);
    const [page, setPage] = useState(1);

    const reloadData = useCallback(() => {
        setReload(prev => prev + 1);
    }, []);

    // Use generic sync hook
    const {
        syncToolbarButtons,
        SyncDialog
    } = useGenericSync(ENTITY_ID_ROLE, reloadData);

    const profileBreadCrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    function openEditDialog(id) {
        setShowModelId(id);
        setShowModal(true);
    }

    async function openDeleteDialog(row) {
        const id = row.id || row;
        const result = await showConfirmDialog("Do you really want to delete the Role?");
        if (result) {
            genericDelete(ENTITY_ID_ROLE, id).then(() => {
                showToast("Role is Deleted!", TOAST_SUCCESS);
                reloadData();
            }).catch(() => {
                }
            ).finally(() => {
                reloadData();
            });
        }
    }

    function addButtonClicked() {
        setShowModelId(null);
        setShowModal(true);
    }

    function handleClose() {
        setShowModal(false);
        reloadData();
    }

    useEffect(() => {
        // Combine sync buttons with page-specific buttons
        const toolBarButtons = [
            ...syncToolbarButtons,
            {
                name: "ADD ROLE",
                color: "primary",
                icon: "fa fa-plus",
                onClick: addButtonClicked,
            },
        ];

        myData.setBaseData({ toolBarButtons });
        pullTheDataForPage();
    }, [reload, syncToolbarButtons]);

    function pullTheDataForPage () {
        let tableFilterData = {};

        setIsLoading(true);
        tableFilterData.page = page;
        tableFilterData.sizePerPage = sizePerPage;
        tableFilterData.currentIndex = 0;

        genericGetAll(ENTITY_ID_ROLE, tableFilterData).then(
            ({data}) => {
                setPageLoadComplete(true);

                setDataList(data);
                setIsLoading(false);
                setPage(1);
            })
            .catch(() => {
                setIsLoading(false);
                setPageLoadComplete(true);
            });
    }

    const rolesMarkup = dataList.entities.map((role) => {
        return (
            <div className="col-md-4" style={{height: "400px"}} key={role.id}>
                <div className="card card-flush h-md-100">
                    <div className="card-header">
                        <div className="card-title">
                            <h2>{role.name}</h2>
                        </div>
                    </div>
                    <div className="card-body pt-1">
                        <div className="text-muted mb-5">
                            This role has the following permissions.
                        </div>
                        <div className="d-flex flex-column text-gray-600">
                            {role.allowedPermissions.slice(0, 4).map((permission) => (
                                // Check if at least one of the permissions is true
                                permission.read || permission.write || permission.create ? (
                                    <div className="d-flex align-items-center py-2" key={permission.id}>
                                        <span className="bullet bg-primary me-3"/> {permission.name} -
                                        {permission.create ? (
                                            <span className="ms-2 fs-8">C,</span>
                                        ) : null}
                                        {permission.read ? (
                                            <span className="ms-2 fs-8">R,</span>
                                        ) : null}
                                        {permission.update ? (
                                            <span className="ms-2 fs-8">U,</span>
                                        ) : null}
                                        {permission.delete ? (
                                            <span className="ms-2 fs-8">D.</span>
                                        ) : null}
                                    </div>
                                ) : null
                            ))}

                            {role.allowedPermissions.length > 4 ? (
                                <div className="d-flex align-items-center py-2">
                                    <div className="text-muted mb-5">
                                        and, many more...
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>


                    <div className="card-footer flex-wrap pt-0">
                        <button
                            type="button"
                            className="btn btn-light btn-active-light-primary m-2"
                            data-bs-toggle="modal"
                            onClick={() => openEditDialog(role.id)}
                            data-bs-target="#kt_modal_update_role">
                            Edit
                        </button>

                        <button
                            type="button"
                            className="btn btn-light btn-active-light-danger m-2"
                            data-bs-toggle="modal"
                            onClick={() => openDeleteDialog(role.id)}
                            data-bs-target="#kt_modal_update_role">
                            Delete
                        </button>
                    </div>
                </div>
            </div>
        );
    });

    const BaseCard = () => (
        <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-5 g-xl-9">
            {pageLoadComplete && rolesMarkup}
        </div>
    );

    return (
        <>
            <div className="card-body mb-4">
                <div
                    className="notice d-flex bg-light-primary rounded border-primary border border-dashed p-6">
                    <i className="ki-duotone ki-information fs-2tx text-primary me-4">
                        <span className="path1"/>
                        <span className="path2"/>
                        <span className="path3"/>
                    </i>
                    <div className="d-flex flex-stack flex-grow-1">
                        <div className="fw-semibold">
                            <div className="fs-6 text-gray-700">
                                <ul>
                                    <li><b>C</b>: Create - Allows the user to create new records.</li>
                                    <li><b>R</b>: Read - Allows the user to read/view existing records.</li>
                                    <li><b>U</b>: Update - Allows the user to update/edit existing records.</li>
                                    <li><b>D</b>: Delete - Allows the user to delete/remove existing records.</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <PageTitle breadcrumbs={profileBreadCrumbs}>Roles</PageTitle>
            <BaseCard/>
            <RoleAddEditDialog show={showModal} handleClose={handleClose} id={showModelId} reloadData={reloadData}/>
            <SyncDialog />
        </>
    );
}
